import { Action, Dispatch } from 'redux';
import { DetailActions, DetailStoreState } from './DetailTypes';
import storeFromArea from '../utils/storeFromArea';

/**
 * Zneplatnění dat.
 * @param area oblast
 * @param id objektu pro invalidaci
 * @returns {{type: string, area: *, data: *}}
 */
export function invalidate(area: string, id: any) {
  return {
    type: DetailActions.INVALIDATE,
    area,
    id
  };
}

/**
 * Filtrování detailu.
 * @param area oblast
 * @param filter filtr
 * @returns {{type: string, area: *, filter: *}}
 */
export function filter(area: string, filter: any) {
  return {
    type: DetailActions.FILTER,
    area,
    filter
  };
}

/**
 * Reset reduceru na původní initial values a stav.
 * @param area oblast
 * @returns {{type: string, area: *}}
 */
export function reset(area: string) {
  return {
    type: DetailActions.RESET,
    area
  };
}

/**
 * Nastavení konkrétních dat předanými, nastavením se aktualizuje i stav fetched a isFetching tak, aby byl výsledek stejný jako po reálném načtení dat ze serveru
 * @param area oblast
 * @param id dat
 *
 * @param data data
 */
export function setData(area: string, id: any, data: any) {
  return (dispatch: Dispatch, getState: Function) => {
    // Načtení aktuálního store
    let store = (storeFromArea(getState(), area) as any) as DetailStoreState;

    // Pokud ve store není potřebné id, dáme ho tam, aby se nám správně spočetl data key
    if (store.id !== id) {
      dispatch(select(area, id));
      store = storeFromArea(getState(), area);
    }

    const dataKey = store.getDataKey.bind(store)();

    // Simulace request dat
    dispatch(request(area, dataKey));

    // Response dat
    dispatch(response(area, data));
  };
}

/**
 * Načtení dat pokud je potřeba.
 * @param area oblast
 * @param id id objektu, které má být ve store
 * @param getData metoda která má vrátit promise pro načtení, vstupem dostane id, předpis: (id, filter) => {}
 */
export function fetchIfNeeded(
  area: string,
  id: any,
  getData: (id: any, filter: any) => Promise<any>
) {
  return (dispatch: Dispatch, getState: Function) => {
    // Načtení aktuálního store
    let store = storeFromArea(getState(), area);

    // Pokud ve store není potřebné id, dáme ho tam, aby se nám správně spočetl data key
    if (JSON.stringify(store.id) !== JSON.stringify(id)) {
      dispatch(select(area, id));
      store = storeFromArea(getState(), area);
    }

    const dataKey = store.getDataKey.bind(store)();
    if (store.currentDataKey !== dataKey) {
      // pokus se data key neschoduje, provedeme fetch
      dispatch(request(area, dataKey));

      const filter = store.filter;
      if (id !== null) {
        // pokud chceme reálně načíst objekt, provedeme fetch přes getData
        getData(id, filter).then(json => {
          const newStore = storeFromArea(getState(), area);
          const newDataKey = newStore.getDataKey.bind(newStore)();
          if (newDataKey === dataKey) {
            // jen pokud příchozí objekt odpovídá dtům, které chceme ve store
            dispatch(response(area, json));
          }
        }); // TODO [stanekpa] - řešit catch a vrácení datakey!
      } else {
        // Response s prázdným objektem
        dispatch(response(area, null));
      }
    }
  };
}

/**
 * Test, předaná akce je akcí pro tuto třídu.
 * @param action akce
 * @returns {boolean} true, pokud action odpovídá této třídě
 */
export function is(action: Action) {
  switch (action.type) {
    case DetailActions.REQUEST:
    case DetailActions.RESPONSE:
    case DetailActions.SELECT:
    case DetailActions.INVALIDATE:
    case DetailActions.FILTER:
    case DetailActions.RESET:
    case DetailActions.UPDATE_VALUE:
      return true;
    default:
      return false;
  }
}

/**
 * Úprava hodnoty ve store - vykonána uživatelem/programem
 * @param area oblast
 * @param data nová data
 * @returns {{type: string, area: *, data: *}}
 */
export function updateValue(area: string, data: any) {
  return {
    type: DetailActions.UPDATE_VALUE,
    area,
    data
  };
}

/**
 * Výběr nového detailu, volá se i v případě, že je zavoláno fetchIfNeeded s jiným id něž ve store.
 * @param area oblast
 * @param id id
 * @returns {{type: string, area: *, id: *}}
 */
function select(area: string, id: any) {
  return {
    type: DetailActions.SELECT,
    area,
    id
  };
}

/**
 * Request dat.
 * @param area oblast
 * @param dataKey data key
 * @returns {{type: string, area: *, dataKey: *}}
 */
function request(area: string, dataKey: any) {
  return {
    type: DetailActions.REQUEST,
    area,
    dataKey
  };
}

/**
 * Response dat.
 * @param area oblast
 * @param data nová data
 * @returns {{type: string, area: *, data: *}}
 */
function response(area: string, data: any) {
  return {
    type: DetailActions.RESPONSE,
    area,
    data
  };
}
